
import { Component, Vue } from "vue-property-decorator";
import { Getter, namespace, State } from "vuex-class";
import { get } from "lodash";

import { GroupStatus } from "@/store/eventqueue/types";
import { ArmDisarmState } from "@/store/arm-disarm-area/types";
import { FeaturesList, Region, UserPermissions, RootState } from "@/store/types";

import { groupsApi } from "@/services/api.groups.service";

import ArmDisarmAreaSelector from "@/components/arm-disarm/ArmDisarmAreaSelector.vue";
import GroupIsArmedModal from "@/components/arm-disarm/GroupIsArmedModal.vue";
import GroupIsDisarmedModal from "@/components/arm-disarm/GroupIsDisarmedModal.vue";
import GroupIsOnTestModal from "@/components/arm-disarm/GroupIsOnTestModal.vue";
import SureViewIcon from "@/components/SureViewIcon.vue";

import RegionSelect from "@/components/form/RegionSelect.vue";
import { getLocalRegion } from "@/utils/region-utils";

const ArmDisarm = namespace("armDisarm");

@Component({
	components: {
		"area-select": ArmDisarmAreaSelector,
		"group-is-armed-modal": GroupIsArmedModal,
		"group-is-disarmed-modal": GroupIsDisarmedModal,
		"group-is-on-test-modal": GroupIsOnTestModal,
		"sureview-icon": SureViewIcon,
		"region-select": RegionSelect
	}
})
export default class SiteArmingDialog extends Vue {
	@State((state: RootState) => state.featuresList) private featuresList: FeaturesList;
	@Getter("getPermissions") private userPermissions: UserPermissions;

	@ArmDisarm.State((state: ArmDisarmState) => state.selectedRegion) private selectedRegion: Region;
	@ArmDisarm.State((state: ArmDisarmState) => state.currentTab) private currentTab: GroupStatus;

	@ArmDisarm.Mutation private setArmDisarmShown: (visible: boolean) => void;
	@ArmDisarm.Mutation private setCurrentTab: (status: GroupStatus) => void;
	@ArmDisarm.Mutation private resetState: () => void;
	@ArmDisarm.Mutation private setSelectedRegion: (region: Region) => void;

	@ArmDisarm.Mutation private setGroupIsArmedModalVisibility: (visible: boolean) => void;
	@ArmDisarm.Mutation private setGroupIsDisarmedModalVisibility: (visible: boolean) => void;
	@ArmDisarm.Mutation private setGroupIsOnTestModalVisibility: (visible: boolean) => void;

	@ArmDisarm.State((state: ArmDisarmState) => state.groupIsArmedModalVisible)
	private groupIsArmedModalVisible: boolean;

	@ArmDisarm.State((state: ArmDisarmState) => state.groupIsDisarmedModalVisible)
	private groupIsDisarmedModalVisible: boolean;

	@ArmDisarm.State((state: ArmDisarmState) => state.groupIsOnTestModalVisible)
	private groupIsOnTestModalVisible: boolean;

	private contentLoading: boolean = false;
	private groupStatuses: { [groupId: number]: GroupStatus } = {};

	/**
	 * Gets the current number of armed groups.
	 */
	private get armedCount(): number {
		return Object.values(this.groupStatuses).filter(status => status == GroupStatus.Armed).length;
	}

	/**
	 * Gets the current number of partially armed groups.
	 */
	private get partiallyArmedCount(): number {
		return Object.values(this.groupStatuses).filter(status => status == GroupStatus.Partial).length;
	}

	/**
	 * Gets the current number of disarmed groups.
	 */
	private get disarmedCount(): number {
		return Object.values(this.groupStatuses).filter(status => status == GroupStatus.Disarmed).length;
	}

	/**
	 * Gets the current number of groups that are on test.
	 */
	private get onTestCount(): number {
		return Object.values(this.groupStatuses).filter(status => status == GroupStatus.OnTest).length;
	}

	/**
	 * Gets a value indicating whether the on test feature is enabled.
	 */
	private get onTestFeatureEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "SeeSiteArmedState", "OnTest"]);
	}

	/**
	 * Gets the list of tabs, and their associated group statuses, to display in the UI.
	 */
	private get tabs(): { title: string; status: GroupStatus }[] {
		var defaultTabs = [
			{ title: "All", status: GroupStatus.All },
			{ title: `Armed (${this.armedCount})`, status: GroupStatus.Armed },
			{ title: `Partial (${this.partiallyArmedCount})`, status: GroupStatus.Partial },
			{ title: `Disarmed (${this.disarmedCount})`, status: GroupStatus.Disarmed }
		];

		if (this.onTestFeatureEnabled) {
			defaultTabs.push({ title: `On Test (${this.onTestCount})`, status: GroupStatus.OnTest });
		}

		return defaultTabs;
	}

	/**
	 * Created handler.
	 */
	private async created(): Promise<void> {
		await this.reload();
	}

	/**
	 * Handler for when the user selects a new region.
	 */
	private async onRegionSelected(selectedRegion: Region) {
		this.setSelectedRegion(selectedRegion);
		await this.reload();
	}

	/**
	 * Fetches the dictionary of group statuses, for the user, in the currently selected region.
	 */
	private async reload() {
		this.contentLoading = true;
		this.groupStatuses = await groupsApi.getStatusesDictionary(this.selectedRegion.url);
		this.contentLoading = false;
	}

	/**
	 * Handler for when the user finishes with one of the arm/disarm/test modals. Resets modal states and reloads
	 * the group statuses.
	 */
	private async resetAndReload(): Promise<void> {
		this.setGroupIsArmedModalVisibility(false);
		this.setGroupIsDisarmedModalVisibility(false);
		this.setGroupIsOnTestModalVisibility(false);
		await this.reload();
	}

	/**
	 * Handler for when the modal is hidden by the user. Resets the region back to the local one.
	 */
	private onModalHidden() {
		this.setSelectedRegion(getLocalRegion());
		this.setArmDisarmShown(false);
	}
}
